import { useAnalytics, withEventContext } from "@codat/analytics";
import { TextArea, Typography, useToast } from "@codat/orchard-ui";
import { useCreateCompanyQuery } from "api";
import { useSourceTypesQuery } from "api/company";
import { ConfigStatusType, useConfig } from "contexts/ConfigContext";
import useQueryParams from "hooks/useQueryParams";
import debounce from "lodash.debounce";
import { useCallback, useEffect, useState } from "react";
import { Redirect, useLocation } from "react-router-dom";
import {
  getPrioritySourceType,
  getSourceTypeLinkUrl,
  getTryCatchErrorMessage,
  getValidationErrorMessage,
  usePageViewedEvent,
} from "utils";

import Button from "components/Button";
import LinkPageRoot from "components/LinkPageRoot";
import TextInput from "components/TextInput";
import { ClientParams } from "config/urls";

import styles from "./CreateCompany.module.css";
import { isEmptyName, validCharacterCheck } from "./name-validation";
import ErrorFallback from "components/ErrorFallback";

export interface CreateCompanyState {
  navigateNext: string;
  companyCreation: {
    body?: string;
    title?: string;
  };
  companyDescriptionConfig: {
    title?: string;
    placeholder?: string;
  };
  locationSearch: string;
  isLoading: boolean;
  linkingNotAuthorized: boolean;
  postCompanyName: (name: string, description?: string) => Promise<void>;
}

export const useCreateCompanyState = (
  params: ClientParams
): CreateCompanyState => {
  const [navigateNext, setNavigateNext] = useState("");
  const [{ loading: createCompanyLoading }, executePost] =
    useCreateCompanyQuery({
      clientId: params.clientId,
    });
  const { addToast } = useToast();
  const { state: config } = useConfig();

  const queryParams = useQueryParams();
  const locationSearch = useLocation().search;

  const companyName = queryParams.get("companyName");
  const createdByUserId = queryParams.get("user");

  const companyCreation =
    config?.data?.pageConfiguration?.companyCreation ?? {};

  const companyDescriptionConfig =
    config?.data?.pageConfiguration?.companyDescription ?? {};

  const [{ loading: sourceTypesLoading }, getSourceTypes] =
    useSourceTypesQuery();

  const postCompanyName = useCallback(
    async (name: string, description?: string) => {
      try {
        const res = await executePost({
          data: {
            name,
            createdByUserId,
            description,
          },
        });
        const sourceTypesResponse = await getSourceTypes({
          companyId: res.data.id,
        });
        const prioritySourceType = getPrioritySourceType(
          sourceTypesResponse.data
        );
        setNavigateNext(getSourceTypeLinkUrl(res.data.id, prioritySourceType));
      } catch (e) {
        const explanation =
          getValidationErrorMessage(e) ?? "Failed to create company";

        addToast({
          message: `${getTryCatchErrorMessage(e)}: ${explanation}`,
          variant: "error",
        });
      }
    },
    [addToast, executePost, getSourceTypes, createdByUserId]
  );

  useEffect(() => {
    if (companyName) {
      postCompanyName(companyName);
    }
  }, [companyName, postCompanyName]);

  usePageViewedEvent({
    ready: config.status === ConfigStatusType.READY,
    clientConfig: config.data,
  });

  const isLoading = sourceTypesLoading || createCompanyLoading;

  const { isNewLinkFlowEnabled, allowUnauthorizedLinking } = config.data;

  const linkingNotAuthorized =
    isNewLinkFlowEnabled && !allowUnauthorizedLinking;

  return {
    navigateNext,
    companyCreation,
    companyDescriptionConfig,
    locationSearch,
    isLoading,
    linkingNotAuthorized,
    postCompanyName,
  };
};

interface CreateCompanyProps {
  match: { params: ClientParams };
  _mockImpl?: (params: ClientParams) => CreateCompanyState;
}

function CreateCompanyContent(props: CreateCompanyProps) {
  const {
    navigateNext,
    companyCreation,
    companyDescriptionConfig,
    locationSearch,
    isLoading,
    linkingNotAuthorized,
    postCompanyName,
  } = (props._mockImpl ?? useCreateCompanyState)(props.match.params);

  const [companyName, setCompanyName] = useState("");
  const [companyDescription, setCompanyDescription] = useState("");
  const analytics = useAnalytics();

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    postCompanyName(companyName, companyDescription);
  };

  const nameContainsInvalid = !validCharacterCheck(companyName);
  const nameIsEmpty = isEmptyName(companyName);
  const nameIsValid = !nameIsEmpty && !nameContainsInvalid;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const sendAnalyticsDebounced = useCallback(
    debounce(analytics.sendEvent, 1000),
    []
  );

  if (linkingNotAuthorized) {
    return (
      <ErrorFallback
        customError="This URL is not valid"
        customStatusCode={400}
      />
    );
  }

  if (navigateNext) {
    return (
      <Redirect
        to={{
          pathname: navigateNext,
          search: locationSearch,
        }}
        push={true}
      />
    );
  }
  return (
    <LinkPageRoot isComplete={true}>
      <form className={styles.form} onSubmit={handleSubmit}>
        {companyCreation?.title && (
          <Typography data-testid="page-title" variant="h3" as="h2">
            {companyCreation?.title}
          </Typography>
        )}
        {companyCreation?.body && (
          <Typography
            data-testid="company-creation-body"
            variant="p"
            as="p"
            className={styles.body}
          >
            {companyCreation?.body}
          </Typography>
        )}
        <TextInput
          className={styles.textInput}
          label="Company name"
          analyticsName="Company name"
          labelClassName={styles.label}
          inputClassName={styles.input}
          id="company-name-input"
          data-testid="company-name-input"
          onChange={(e) => setCompanyName(e.target.value)}
          value={companyName}
          error={nameContainsInvalid}
          errorMessage={
            nameContainsInvalid ? "Contains invalid characters" : undefined
          }
        />
        {companyDescriptionConfig?.title && (
          <div data-testid="company-description">
            <TextArea
              label={companyDescriptionConfig.title}
              labelClassName={styles.label}
              id="company-description-input"
              onChange={(e) => {
                sendAnalyticsDebounced(
                  `Company Description text area - Typed`,
                  {
                    value: e.target.value,
                  }
                );
                setCompanyDescription(e.target.value);
              }}
              value={companyDescription}
              placeholder={companyDescriptionConfig.placeholder}
            />
          </div>
        )}
        <div className={styles.button}>
          <Button
            isLoading={isLoading}
            data-testid="primary-next-btn"
            type="submit"
            label="Next"
            analyticsName="Next"
            disabled={!nameIsValid}
          />
        </div>
      </form>
    </LinkPageRoot>
  );
}

export const CreateCompany = withEventContext<
  CreateCompanyProps & JSX.IntrinsicAttributes
>(CreateCompanyContent, "Create company page");
